// src/components/pages/AssignEvaluations.jsx
import React, { useMemo, useState } from "react";
import {
  assignCourses,
  initialQuestionnaires,
} from "../Stores/assignEvaluacionesStore";

const AssignEvaluations = () => {
  // 1) Crear cuestionario
  const [questionnaires, setQuestionnaires] = useState(initialQuestionnaires);
  const [createForm, setCreateForm] = useState({
    titulo: "",
    tipo: "Normativo",
    version: "2025",
    descripcion: "",
  });
  const [createMessage, setCreateMessage] = useState(null);

  // 2) Asignar cuestionario → curso / módulo
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState(
    initialQuestionnaires[0]?.id || ""
  );
  const [selectedCourseId, setSelectedCourseId] = useState(
    assignCourses[0]?.id || ""
  );
  const [selectedModuleId, setSelectedModuleId] = useState(
    assignCourses[0]?.modules[0]?.id || ""
  );
  const [assignments, setAssignments] = useState([]);

  const currentCourse = useMemo(
    () => assignCourses.find((c) => c.id === selectedCourseId) || null,
    [selectedCourseId]
  );
  const currentModules = currentCourse?.modules || [];

  // --- handlers creación cuestionario ---

  const handleCreateChange = (field) => (e) => {
    const value = e.target.value;
    setCreateForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreateQuestionnaire = (e) => {
    e.preventDefault();
    setCreateMessage(null);

    const titulo = createForm.titulo.trim();
    if (!titulo) {
      setCreateMessage("El cuestionario debe tener un título.");
      return;
    }

    const newId = `q-${Date.now()}`; // id demo
    const nuevo = {
      id: newId,
      titulo,
      tipo: createForm.tipo || "Normativo",
      version: createForm.version || "2025",
      descripcion: createForm.descripcion || "",
    };

    setQuestionnaires((prev) => [...prev, nuevo]);
    setSelectedQuestionnaireId(newId);
    setCreateForm({
      titulo: "",
      tipo: "Normativo",
      version: "2025",
      descripcion: "",
    });
    setCreateMessage("Cuestionario creado (demo). Ahora asígnalo a un curso.");
  };

  // --- handlers asignación ---

  const handleCourseChange = (e) => {
    const newCourseId = e.target.value;
    setSelectedCourseId(newCourseId);

    const course = assignCourses.find((c) => c.id === newCourseId);
    const firstModule = course?.modules[0]?.id || "";
    setSelectedModuleId(firstModule);
  };

  const handleAssign = (e) => {
    e.preventDefault();

    if (!selectedQuestionnaireId || !selectedCourseId || !selectedModuleId) {
      return;
    }

    const q = questionnaires.find((x) => x.id === selectedQuestionnaireId);
    const course = assignCourses.find((c) => c.id === selectedCourseId);
    const module = course?.modules.find((m) => m.id === selectedModuleId);

    if (!q || !course || !module) return;

    // evitar duplicados exactos
    const exists = assignments.some(
      (a) =>
        a.cuestionarioId === q.id &&
        a.courseId === course.id &&
        a.moduleId === module.id
    );
    if (exists) return;

    const nuevo = {
      id: `asig-${Date.now()}-${assignments.length}`,
      cuestionarioId: q.id,
      cuestionarioTitulo: q.titulo,
      tipo: q.tipo,
      version: q.version,
      courseId: course.id,
      courseNombre: course.nombre,
      moduleId: module.id,
      moduleNombre: module.nombre,
      estado: "Activo",
    };

    setAssignments((prev) => [nuevo, ...prev]);
  };

  return (
    <div className="ae-page">
      <div className="profile-header-title">
        <h1>Asignar evaluaciones</h1>
      </div>
      <p className="ae-subtitle">
        Crea cuestionarios y asígnalos a los cursos y módulos que administras.
      </p>

      {/* columnas principales */}
      <div className="ae-columns">
        {/* 1. Crear cuestionario */}
        <section className="ae-card">
          <h2 className="ae-section-title">1. Crear nuevo cuestionario</h2>
          <p className="ae-section-help">
            Define los datos generales del cuestionario. En esta demo no se
            editan preguntas, solo la ficha del cuestionario.
          </p>

          <form onSubmit={handleCreateQuestionnaire} className="ae-form">
            <div className="ae-form-row">
              <label className="ae-label">Título del cuestionario</label>
              <input
                type="text"
                className="ae-input"
                value={createForm.titulo}
                onChange={handleCreateChange("titulo")}
                placeholder="Ej. Evaluación general de Trabajo en Equipo"
              />
            </div>

            <div className="ae-form-row ae-form-row-inline">
              <div className="ae-form-col">
                <label className="ae-label">Tipo</label>
                <select
                  className="ae-input"
                  value={createForm.tipo}
                  onChange={handleCreateChange("tipo")}
                >
                  <option value="Normativo">Normativo</option>
                  <option value="Operativo">Operativo</option>
                  <option value="Inducción">Inducción</option>
                </select>
              </div>
              <div className="ae-form-col ae-form-col-small">
                <label className="ae-label">Versión</label>
                <input
                  type="text"
                  className="ae-input"
                  value={createForm.version}
                  onChange={handleCreateChange("version")}
                />
              </div>
            </div>

            <div className="ae-form-row">
              <label className="ae-label">Descripción (opcional)</label>
              <textarea
                className="ae-input ae-textarea"
                rows={3}
                value={createForm.descripcion}
                onChange={handleCreateChange("descripcion")}
                placeholder="Resumen breve del objetivo de este cuestionario."
              />
            </div>

            {createMessage && (
              <div className="ae-message ae-message-info">
                {createMessage}
              </div>
            )}

            <div className="ae-actions">
              <button type="submit" className="qe-btn qe-btn-primary">
                Guardar cuestionario (demo)
              </button>
            </div>
          </form>

          <div className="ae-existing-list">
            <h3 className="ae-existing-title">Cuestionarios existentes</h3>
            <ul className="ae-existing-ul">
              {questionnaires.map((q) => (
                <li key={q.id} className="ae-existing-item">
                  <span className="ae-existing-name">{q.titulo}</span>
                  <span className="ae-existing-meta">
                    {q.tipo} • Versión {q.version}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* 2. Asignar cuestionario a curso/módulo */}
        <section className="ae-card">
          <h2 className="ae-section-title">
            2. Asignar cuestionario a un curso
          </h2>
          <p className="ae-section-help">
            Selecciona el cuestionario, el curso y el módulo donde estará
            disponible para los alumnos.
          </p>

          <form onSubmit={handleAssign} className="ae-form">
            <div className="ae-form-row">
              <label className="ae-label">Cuestionario</label>
              <select
                className="ae-input"
                value={selectedQuestionnaireId}
                onChange={(e) => setSelectedQuestionnaireId(e.target.value)}
              >
                {questionnaires.map((q) => (
                  <option key={q.id} value={q.id}>
                    {q.titulo}
                  </option>
                ))}
              </select>
            </div>

            <div className="ae-form-row">
              <label className="ae-label">Curso</label>
              <select
                className="ae-input"
                value={selectedCourseId}
                onChange={handleCourseChange}
              >
                {assignCourses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nombre} ({c.clave})
                  </option>
                ))}
              </select>
            </div>

            <div className="ae-form-row">
              <label className="ae-label">Módulo del curso</label>
              <select
                className="ae-input"
                value={selectedModuleId}
                onChange={(e) => setSelectedModuleId(e.target.value)}
              >
                {currentModules.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.nombre}
                  </option>
                ))}
              </select>
            </div>

            <div className="ae-actions">
              <button type="submit" className="qe-btn qe-btn-secondary">
                Asignar cuestionario al curso
              </button>
            </div>
          </form>

          <div className="ae-assignments">
            <h3 className="ae-existing-title">Asignaciones realizadas (demo)</h3>

            {assignments.length === 0 ? (
              <p className="ae-empty">Aún no hay cuestionarios asignados.</p>
            ) : (
              <table className="ae-table">
                <thead>
                  <tr>
                    <th>Curso</th>
                    <th>Módulo</th>
                    <th>Cuestionario</th>
                    <th>Tipo</th>
                    <th>Versión</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {assignments.map((a) => (
                    <tr key={a.id}>
                      <td>{a.courseNombre}</td>
                      <td>{a.moduleNombre}</td>
                      <td>{a.cuestionarioTitulo}</td>
                      <td>{a.tipo}</td>
                      <td>{a.version}</td>
                      <td>
                        <span className="ae-status-pill">{a.estado}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default AssignEvaluations;
